<template>
  <router-link
    v-if="data.buttonLink || data.buttonPath"
    :to="data.routerObject"
    :class="[
      sizeClasses(),
      data.buttonClass,
      `cursor-pointer ${hoverClass} inline-block rounded text-center ease-in duration-300 focus:outline-none`,
    ]"
    :style="{ 'background-color': `${data.buttonColor}` }"
  >
    <slot />
  </router-link>

  <button
    v-else
    v-bind="$attrs"
    type="button"
    :class="[sizeClasses(), defaultClasses(), data.buttonClass]"
    :style="{ 'background-color': `${data.buttonColor}` }"
    :disabled="data.isDisabled"
  >
    <slot />
  </button>
</template>

<script lang="ts">
import { defineComponent, reactive, watch, onBeforeMount } from 'vue';

type RouterObject = {
  name?: string;
  path?: string;
  params?: object;
};

type DataObject = {
  buttonClass?: string;
  buttonColor?: string;
  buttonLink?: string;
  buttonPath?: string;
  params?: object;
  routerObject: RouterObject;
  isDisabled?: boolean;
  tooltip?: string;
  tooltipPlacement?: any;
};

export default defineComponent({
  name: 'BrandButton',
  props: {
    params: Object,
    buttonLink: String,
    buttonPath: String,
    buttonClass: {
      type: String,
      default: 'bg-pri text-white',
    },
    buttonColor: {
      type: String,
    },
    size: {
      type: String,
      default: 'md',
    },
    hoverClass: {
      type: String,
      default: 'hover:opacity-75',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  components: {},

  setup(props) {
    const data = reactive<DataObject>({
      buttonClass: props.buttonClass,
      buttonColor: props.buttonColor,
      buttonLink: props.buttonLink,
      buttonPath: props.buttonPath,
      params: props.params,
      routerObject: {},
      isDisabled: props.isDisabled,
    });

    onBeforeMount(() => {
      if (data.buttonLink) {
        data.routerObject.name = data.buttonLink;
      }

      if (data.buttonPath) {
        data.routerObject.path = data.buttonPath;
      }

      if (data.params) {
        data.routerObject.params = data.params;
      }
    });

    watch(
      () => props,
      () => {
        data.buttonColor = props.buttonColor;
        data.buttonClass = props.buttonClass;
        data.isDisabled = props.isDisabled;
      },
      { deep: true },
    );

    const sizeClasses = () => {
      switch (props.size) {
        case 'xl-px-0':
          return 'px-0 py-4 text-2xl';
        case 'lg-px-0':
          return 'px-0 py-3';
        case 'sm-px-0':
          return 'px-0 py-2 text-sm';
        case 'xs-px-0':
          return 'px-0 py-1 text-xs';
        case 'md-px-0':
          return 'px-0 py-2';
        case 'xxl':
          return 'px-12 py-4 text-3xl';
        case 'xl':
          return 'px-12 py-4 text-2xl';
        case 'lg':
          return 'px-8 py-3';
        case 'sm':
          return 'px-4 py-2 text-sm';
        case 'xs':
          return 'px-2 py-1 text-xs';
        case 'md':
        default:
          return 'px-6 py-2';
      }
    };

    const defaultClasses = () => {
      let classes = `cursor-pointer ${props.hoverClass} inline-block text-center ease-in duration-300 focus:outline-none `;

      if (data.isDisabled) {
        classes = `${classes} bg-gray-300 hover:opacity-100 text-gray-600 `;
      }

      return props.buttonClass?.includes('rounded') ? classes : classes + 'rounded';
    };

    const getPopupContainer = (trigger: HTMLElement) => {
      return trigger.parentElement;
    };

    return {
      data,
      sizeClasses,
      defaultClasses,
      getPopupContainer,
    };
  },
});
</script>
